import React, { useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
import { USER_STATUS_QUERY } from 'Queries/Queries';
import { format, parse } from 'date-fns';
import { setNestedObjectValues } from 'formik';
import { useInstallerStatusSummary } from 'hooks/UseInstallerStatusSummary';
import PropTypes from 'prop-types';

import { AgreementSection } from 'components/DebFormComponents/AgreementSection';
import { MissingExpertDataPopup } from 'components/DebFormComponents/Popup/MissingExpertDataPopup';
import { ContractsTable } from 'components/DebFormComponents/Table/ContractsTable';
import { ExpertTable } from 'components/DebFormComponents/Table/ExpertTable';
import { SealsTable } from 'components/DebFormComponents/Table/SealsTable';
import { FormActionButtons } from 'components/FormHelper/FormActionButtons';
import { FormPageWrapper } from 'components/FormHelper/FormPageWrapper';
import { FormSection } from 'components/FormHelper/FormSection';
import { HintText } from 'components/HintText/HintText';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { PageTitle } from 'components/PageTitle/PageTitle';

import {
  BRANCH_TYPE,
  DEB_ACTIVE_PAGE,
  INSTALLER_ENTRIES,
  STAGE,
  TENANT_ID,
} from 'global/constants';
import { evaluateCondition } from 'global/fieldValidationFunction';

import { FFormDebug } from 'utils/FFormDebug';
import { checkKeyDown } from 'utils/FormUtils';
import {
  expertHasUnextendedBranch,
  hasGuestBranchExtended,
  isRegisteredBranchGuest,
  isRegisteredInstallerOnlyHilfsbetrieb,
} from 'utils/InstallerUtils';
import { getStage } from 'utils/StageUtils';
import { getTenantId } from 'utils/tenantUtil';

import { InstallerContactSection } from './InstallerContactSection';
import { SectionContent } from './SectionContent';
import { DEB_FIRST_PAGE_SECTIONS, checkErrors } from './Utils/DebFormSectionUtil';
import { ERRORS } from './Utils/Errors';

import './DebForm.sass';

export const DebFormFirstPage = ({
  formik,
  initialValues,
  installer,
  energyAgreementLink,
  gasAgreementLink,
  setPage,
  comboBoxOptions,
}) => {
  const [buttonErrorMessage, setButtonErrorMessage] = useState(ERRORS.DEFAULT);
  const [missingExpertWarningShown, setMissingExpertWarningShown] = useState(false);
  const [showMissingExpertPopup, setShowMissingExpertPopup] = useState(false);
  const [savedExpertsData, setSavedExpertsData] = useState(
    initialValues.experts ? initialValues.experts : []
  );
  const [savedInstallerContactValues, setSavedInstallerContactValues] = useState({
    phone: initialValues.phone,
    mobile: initialValues.mobile,
    email: initialValues.email,
  });

  const { values, errors, setTouched, touched } = formik;
  const installerStatusSummary = useInstallerStatusSummary(installer);
  const tenantId = getTenantId();
  const { data: userStatusData, loading: userStatusLoading } = useQuery(USER_STATUS_QUERY, {
    gcTime: 0,
    fetchPolicy: 'network-only',
  });
  const userStatus = userStatusData && userStatusData.userStatus;

  // needed to set touched on fields that have errors on mount (via API data)
  useEffect(() => {
    setTouched(setNestedObjectValues(errors, true));
  }, [errors]);

  useEffect(() => {
    const requiredFields = formik.values.doesGasBranchExist + formik.values.doesEnergyBranchExist;
    // AVA extra postal code field is not considered a change but is a required field for GuestOnly
    const copiedValues = { ...values };
    delete copiedValues.avaGuestPostal;
    const effectiveInitialValues = {
      ...formik.initialValues,
      ...savedInstallerContactValues,
      experts: savedExpertsData,
    };
    const error = checkErrors(copiedValues, effectiveInitialValues, errors, requiredFields);
    setButtonErrorMessage(error);
  }, [values, errors, savedInstallerContactValues, savedExpertsData]);

  useEffect(() => {
    if (isRegisteredBranchGuest(installerStatusSummary, BRANCH_TYPE.ENERGY)) {
      const hasExtended = hasGuestBranchExtended(
        formik.values.experts,
        initialValues.experts,
        BRANCH_TYPE.ENERGY
      );
      formik.setFieldValue('energyExtension', hasExtended);
    }
    if (isRegisteredBranchGuest(installerStatusSummary, BRANCH_TYPE.GAS)) {
      const hasExtended = hasGuestBranchExtended(
        formik.values.experts,
        initialValues.experts,
        BRANCH_TYPE.GAS
      );
      formik.setFieldValue('gasExtension', hasExtended);
    }
  }, [values.experts]);

  // True when at least one expert still has a visible, unclicked "Verlängern" button.
  const hasMissingExpertData =
    tenantId === TENANT_ID.BAG &&
    values.experts?.some((expert) => {
      const oldExpert = initialValues.experts?.find((e) => e.id === expert.id);
      return expertHasUnextendedBranch(expert, oldExpert, installerStatusSummary);
    });

  const handleWeiterClick = () => {
    if (hasMissingExpertData && !missingExpertWarningShown) {
      setShowMissingExpertPopup(true);
      setMissingExpertWarningShown(true);
    } else {
      setPage(DEB_ACTIVE_PAGE.FORM_UPLOAD);
    }
  };

  const cleanedDateString =
    userStatus &&
    userStatus.latestDebFormRequestDate &&
    userStatus.latestDebFormRequestDate.replace('T', ' ');
  if (userStatusLoading) {
    return <LoadingIndicator />;
  }

  getStage() !== STAGE.PRODUCTION && console.log(formik);

  return (
    <form
      onKeyDown={checkKeyDown}
      onSubmit={(e) => {
        e.preventDefault();
      }}
      className="deb-form__first-page">
      <FormPageWrapper>
        <div className="deb-form__title-section">
          <PageTitle text="Eintragung bearbeiten und verlängern" />
          <div>
            Hier können Sie Ihre in unserem Installateurverzeichnis hinterlegten Daten bearbeiten
            und ggfs. auch Ihre Eintragung verlängern.
          </div>
          <br />
          {cleanedDateString && (
            <HintText>
              <div>
                Ihre letzte Anfrage wurde gesendet am{' '}
                <b>
                  {format(
                    parse(cleanedDateString, 'yyyy-MM-dd HH:mm:ss.SSSSSS', new Date()),
                    'dd.MM.yyyy'
                  )}{' '}
                </b>
                um{' '}
                <b>
                  {format(
                    parse(cleanedDateString, 'yyyy-MM-dd HH:mm:ss.SSSSSS', new Date()),
                    'HH:mm'
                  )}{' '}
                  Uhr
                </b>
              </div>
            </HintText>
          )}
        </div>
        {DEB_FIRST_PAGE_SECTIONS.map((section) => {
          const evaluateConditionOptions = {
            isOnlyHilfsbetrieb: isRegisteredInstallerOnlyHilfsbetrieb(installerStatusSummary),
            installerStatusSummary,
            values,
          };
          if (!evaluateCondition(section.whenShouldSectionRender, evaluateConditionOptions)) {
            return null;
          }
          if (section.isInstallerContact) {
            if (section.id === 'companyContacts') {
              return (
                <InstallerContactSection
                  key="installerContact"
                  formik={formik}
                  onSaved={(vals) =>
                    setSavedInstallerContactValues((prev) => ({
                      ...prev,
                      ...vals,
                    }))
                  }
                  initialValues={initialValues}
                />
              );
            }
            return null;
          }
          return (
            <FormSection
              key={section.id}
              title={section.title}
              subtitle={section.subtitle}
              tooltip={section.tooltip}
              navigationTooltip={section.navigationTooltip}
              infoText={section.infoText}>
              <SectionContent
                section={section}
                formik={formik}
                values={values}
                initialValues={initialValues}
                comboBoxOptions={comboBoxOptions}
                evaluateConditionOptions={evaluateConditionOptions}
              />
            </FormSection>
          );
        })}
        {tenantId === TENANT_ID.BAG &&
          !(installerStatusSummary.installerEntry === INSTALLER_ENTRIES.GUEST_ONLY) && (
            <FormSection
              title="Zusatzverträge, Plombenzangen bzw. -stempel und Nüsse"
              subtitle="Ausgegebene Plombenstempel/Plombenzangen/Nüsse">
              <SealsTable
                data={values.seals ? values.seals : []}
                oldData={initialValues.seals ? initialValues.seals : []}
                formik={formik}
              />
              <div>
                Sie haben noch weitere, von uns an Sie ausgegebene
                Plombenstempel/Plombenzangen/Nüsse, die hier nicht aufgelistet sind? Dann teilen Sie
                uns dies bitte beim nächsten Schritt in dem Kommentarfeld mit.
              </div>
            </FormSection>
          )}
        {[TENANT_ID.AVA, TENANT_ID.BAG].includes(tenantId) && (
          <FormSection subtitle="Verträge">
            <ContractsTable
              data={values.contracts ? values.contracts : []}
              oldData={formik.initialValues.contracts ? formik.initialValues.contracts : []}
              formik={formik}
            />
          </FormSection>
        )}
        <FormSection>
          <ExpertTable
            installer={installer}
            data={values.experts || []}
            oldData={savedExpertsData}
            formik={formik}
            installerStatusSummary={installerStatusSummary}
            showMissingWarning={missingExpertWarningShown && hasMissingExpertData}
            onExpertContactSaved={({ id, telefon, netzportalEmail }) =>
              setSavedExpertsData((prev) =>
                prev.map((e) => (e.id === id ? { ...e, telefon, netzportalEmail } : e))
              )
            }
          />
        </FormSection>

        <FormSection>
          <AgreementSection
            formik={formik}
            installerStatusSummary={installerStatusSummary}
            energyAgreementLink={energyAgreementLink}
            gasAgreementLink={gasAgreementLink}
          />
        </FormSection>
      </FormPageWrapper>
      <FormActionButtons
        hasErrors={Boolean(buttonErrorMessage)}
        titleLeft="Zurücksetzen"
        titleRight="Weiter"
        errorMessage={buttonErrorMessage}
        onClickLeft={() => {
          formik.resetForm();
          window.scrollTo(0, 0);
        }}
        onClickRight={handleWeiterClick}
      />
      <FFormDebug values={values} errors={errors} touched={touched} />
      <MissingExpertDataPopup
        isOpen={showMissingExpertPopup}
        onClose={() => setShowMissingExpertPopup(false)}
      />
    </form>
  );
};

DebFormFirstPage.displayName = 'DebFormFirstPage';

DebFormFirstPage.propTypes = {
  formik: PropTypes.object.isRequired,
  initialValues: PropTypes.object.isRequired,
  installer: PropTypes.object.isRequired,
  energyAgreementLink: PropTypes.string,
  gasAgreementLink: PropTypes.string,
  setPage: PropTypes.func.isRequired,
  comboBoxOptions: PropTypes.shape({
    craftChambers: PropTypes.array,
    gridnetProviders: PropTypes.array,
  }),
};

DebFormFirstPage.defaultProps = {
  energyAgreementLink: '',
  gasAgreementLink: '',
  comboBoxOptions: {
    craftChambers: [],
    gridnetProviders: [],
  },
};
